// JournalingService.js - Contains only the API calls and helper functions (no React components)
import API_URL from '../../api/config.js';
/**
 * Starts a new journaling session for a trade
 * @param {Object} trade - The trade to journal
 * @returns {Promise} - Session data with sessionId and aiMessage
 */


export const startJournalingSession = async (trade) => {
  try {
    // Get token from localStorage (or wherever you store it)
    const token = localStorage.getItem('access_token');
    console.log('Auth Token: first', token);

    const response = await fetch(`${API_URL}/journaling/start`, { 
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` // Add the Authorization header
      },
      body: JSON.stringify({ trade })
    });
    
    if (!response.ok) {
      // Handle non-2xx responses better
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error starting journaling session:', error);
    throw error;
  }
};
/**
 * Sends a message in a journaling session
 * @param {String} sessionId - The active session ID
 * @param {String} message - The message to send
 * @returns {Promise} - Response with AI message
 */
export const sendJournalingMessage = async (sessionId, message) => {
  const token = localStorage.getItem('access_token'); // Update this to match your token storage method

  try {
      const response = await fetch(`${API_URL}/journaling/autosave`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`   // Add the Authorization header
     },
      body: JSON.stringify({ sessionId, message }), 
    
    });
    const data = await response.json();
    if (data.error) throw new Error(data.error);
    return data;
  } catch (error) {
    console.error('Error sending journaling message:', error);
    throw error;
  }
};


export const sendTradeToDB = async (trade) => {
  const token = localStorage.getItem('access_token');
  console.log("about to try and send trade to db", trade);
  try {
    // Transform frontend trade object to match backend schema
    const tradeData = {
      symbol: trade.symbol,
      direction: trade.direction,
      entry_price: parseFloat(trade.entry),
      exit_price: parseFloat(trade.exit),
      lot_size: parseFloat(trade.lotSize),
      profit: parseFloat(trade.profit),
      pips: parseFloat(trade.pips),
      notes: trade.notes,
      // Use the new datetime fields
      openTime: trade.openTime,
      closeTime: trade.closeTime,
      trade_date: trade.openTime, // Use openTime as the trade date
      // Add default values for required fields missing from the form
      orderType: 'market',
      asset_type: 'forex',
      currency: 'USD',
      commission: parseFloat(trade.commission || 0),
      swap: parseFloat(trade.swap || 0),
      stop_loss: trade.stopLoss ? parseFloat(trade.stopLoss) : null,
      take_profit: trade.takeProfit ? parseFloat(trade.takeProfit) : null,
    };
    
    // Ensure no 'id' field is sent for new trades
    delete tradeData.id;
    
    const response = await fetch(`${API_URL}/trades`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(tradeData)
    });
    const data = await response.json();
    if (data.error || !response.ok) {
      throw new Error(data.error || 'Failed to save trade to DB');
    }
    
    // The original function returned analysis, which is not present here.
    // We will return the saved trade data.
    return data.data;

  } catch (error) {
    console.error('Error saving trade to DB:', error);
    throw error;
  }
};

export const fetchTradesFromDB = async () => {
  const token = localStorage.getItem('access_token');
  try {
    const response = await fetch(`${API_URL}/trades`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.error) throw new Error(data.error);
    
    // Transform the data to include analysis fields
    // PostgreSQL lowercases column names, so normalize opentime/closetime to camelCase
    return data.data.map(trade => ({
      ...trade,
      openTime: trade.openTime || trade.opentime || null,
      closeTime: trade.closeTime || trade.closetime || null,
      analysis: {
        strategy: trade.analysis?.strategy || 50,
        risk: trade.analysis?.risk || 50,
        emotion: trade.analysis?.emotion || 50,
        notes: trade.analysis?.notes || ''
      }
    }));
  } catch (error) {
    console.error('Error fetching trades from DB:', error);
    throw error;
  }
};

/**
 * Ends a journaling session
 * @param {String} sessionId - The session to end
 * @returns {Promise} - Session end status
 */
export const endJournalingSession = async (sessionId) => {
  try {
    const response = await fetch(`${API_URL}/journaling/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId })
    });
    return await response.json();
  } catch (error) {
    console.error('Error ending journaling session:', error);
    // Return success even on error to allow UI to proceed
    return { success: true };
  }
};

/**
 * Generates a summary for a journaling session
 * @param {String} sessionId - The session to summarize
 * @returns {Promise} - Summary data
 */
export const generateJournalingSummary = async (sessionId) => {
  try {
    const response = await fetch(`${API_URL}/journaling/summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId })
    });
    const data = await response.json();
    if (data.error) throw new Error(data.error);
    return data;
  } catch (error) {
    console.error('Error generating journaling summary:', error);
    throw error;
  }
};

/**
 * Creates a new journal object
 * @param {Object} trade - The trade to journal
 * @param {String} sessionId - Session ID
 * @param {Array} messages - Initial messages
 * @param {Boolean} isJournaling - Whether journaling is active
 * @param {Boolean} started - Whether the session has started
 * @returns {Object} - Journal object
 */
export const createJournalObject = (
  trade, 
  sessionId = 'pending_' + trade.id, 
  messages = [], 
  isJournaling = false, 
  started = false
) => {
  return {
    trade,
    sessionId,
    messages,
    isJournaling,
    started
  };
};

/**
 * Updates a journal in a list by sessionId
 * @param {Array} journals - List of journals
 * @param {String} sessionId - Session ID to update
 * @param {Object} updates - Properties to update
 * @param {Boolean} replaceSessionId - Whether to replace the session ID
 * @returns {Array} - Updated journals list
 */
export const updateJournalInList = (journals, sessionId, updates, replaceSessionId = false) => {
  return journals.map(journal => {
    if (journal.sessionId === sessionId) {
      if (replaceSessionId && updates.sessionId) {
        return { ...journal, ...updates, sessionId: updates.sessionId };
      }
      return { ...journal, ...updates };
    }
    return journal;
  });
};

export const updateTradeInDB = async (tradeId, trade) => {
  const token = localStorage.getItem('access_token');
  try {
    // Extract analysis data to be stored separately
    const { analysis, ...tradeData } = trade;
    
    const response = await fetch(`${API_URL}/trades/${tradeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(tradeData)
    });
    const data = await response.json();
    if (data.error) throw new Error(data.error);
    
    // Add analysis back to the response
    return {
      ...data.data,
      analysis
    };
  } catch (error) {
    console.error('Error updating trade in DB:', error);
    throw error;
  }
};

export const deleteTradeFromDB = async (tradeId) => {
  const token = localStorage.getItem('access_token');
  try {
    const response = await fetch(`${API_URL}/trades/${tradeId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.error) throw new Error(data.error);
    return data;
  } catch (error) {
    console.error('Error deleting trade from DB:', error);
    throw error;
  }
};